import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, CircularProgress, Chip, alpha, useTheme, Grid, Skeleton, Stack,
} from "@mui/material";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LaptopMacOutlinedIcon from "@mui/icons-material/LaptopMacOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import PriorityHighOutlinedIcon from "@mui/icons-material/PriorityHighOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import useUser from "../../Core/hook/useUser";
import { useGetMyStaffCalendarQuery, useGetChatUnreadCountQuery, useGetMyAssessmentsQuery, useGetMyMessagesQuery, useGetMyParentRequestsQuery } from "../../Api/attoDeskApi";
import { DASH_C, DASH_NEUTRAL } from "./shared/palette";

/**
 * Staff Portal — Phase 2 "My Day" dashboard.
 *
 * Data source is the new self-only /staff-portal/my-calendar endpoint
 * (see TeamController::getMyCalendar) which returns enriched events
 * with `scheduled_start_at` / `scheduled_end_at` (ISO), `actual_*_at`,
 * `ended_by`, and a derived `attendance_state` enum. Nothing on this
 * page is fabricated — every count / row / chip is a projection of
 * data the current staff user is authorised to see.
 *
 * Deferred (Phase 3+): full My Students staff-safe profile, canonical
 * staffCanAccessStudent helper suite, full assessments / assignments
 * workspaces, staff-scoped Parent Requests summary (no safe endpoint
 * exists yet — the audit surfaced this), full ABA workspace redesign.
 */

// ── formatters ─────────────────────────────────────────────────────
const fmtDate = (d: Date) =>
  d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
const fmtTime = (iso: string | null | undefined) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};
const toYmd = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const fmtCountdown = (ms: number): string => {
  const mins = Math.max(0, Math.round(ms / 60000));
  if (mins < 1) return "any moment";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem === 0 ? `${h} hr` : `${h} hr ${rem} min`;
};

// ── attendance_state visual mapping ────────────────────────────────
type AttState = "upcoming" | "live" | "pending" | "submitted" | "cancelled";
const stateChip = (s: AttState) => {
  switch (s) {
    case "live":      return { label: "Live", bg: "#dcfce7", fg: "#166534", border: "#a7f3d0" };
    case "pending":   return { label: "Attendance Pending", bg: "#fef3c7", fg: "#92400e", border: "#fde68a" };
    case "submitted": return { label: "Submitted", bg: "#eef2ff", fg: "#3730a3", border: "#c7d2fe" };
    case "cancelled": return { label: "Cancelled", bg: "#fee2e2", fg: "#991b1b", border: "#fecaca" };
    case "upcoming":
    default:          return { label: "Upcoming", bg: "#f1f5f9", fg: "#475569", border: "#e2e8f0" };
  }
};

// ── compact class label ────────────────────────────────────────────
const compactClassLabel = (e: any): string => {
  const grades: string[] = Array.isArray(e?.grades) ? e.grades : [];
  const subjects: string[] = Array.isArray(e?.subjects) ? e.subjects : [];
  const gLabel = (g: string) => (/^(pre-?k|jk|sk|adult|\d+)$/i.test(g) ? `Grade ${g}` : g);
  if (grades.length === 1 && subjects.length > 0 && subjects.length <= 3) {
    return `${gLabel(grades[0])} — ${subjects.join(", ")}`;
  }
  if (grades.length > 1 || subjects.length > 3) {
    return `${grades.length || 0} Grade${grades.length === 1 ? "" : "s"} · ${subjects.length} Subject${subjects.length === 1 ? "" : "s"}`;
  }
  if (grades.length === 1) return gLabel(grades[0]);
  if (subjects.length > 0) return subjects[0];
  return "Class";
};

const StaffPortal: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userData, staffId, isLoading: userLoading } = useUser();

  const todayStart = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const todayEnd = useMemo(() => { const d = new Date(); d.setHours(23, 59, 59, 999); return d; }, []);
  // Aug 2026 — extend the fetch window seven days ahead so the
  // dashboard can surface a "Next Class" that lands tomorrow (or
  // later this week) and a "Coming Up" block. The earlier window
  // was today-only, which caused a legitimate empty-Sunday to
  // render "no upcoming class" even when Monday had a full roster.
  const windowEnd = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(23, 59, 59, 999); return d; }, []);
  const tomorrowStart = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(0, 0, 0, 0); return d; }, []);
  const tomorrowEnd = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(23, 59, 59, 999); return d; }, []);
  const todayYmd = useMemo(() => toYmd(todayStart), [todayStart]);
  const tomorrowYmd = useMemo(() => toYmd(tomorrowStart), [tomorrowStart]);

  // Self-only. Poll every 60s per spec §37.
  const { data, isLoading, isError } = useGetMyStaffCalendarQuery(
    { start: toYmd(todayStart), end: toYmd(windowEnd) },
    { skip: !userData, pollingInterval: 60_000 }
  );
  // Phase 3c — prefer the staff-scoped inbox unread count over the
  // legacy chat count. useGetMyMessagesQuery(per_page:1) returns
  // counts.unread which is participant-filtered by Auth::id — no
  // school-wide leak, matches what the /staff-portal/messages page
  // shows. Fall back to the legacy chat endpoint if the new field
  // isn't populated yet (defensive during rollout).
  const { data: staffMsgsData } = useGetMyMessagesQuery({ per_page: 1 }, { skip: !userData, pollingInterval: 60_000 });
  const { data: unreadData } = useGetChatUnreadCountQuery(undefined, {
    skip: !userData, pollingInterval: 60_000,
  });
  const unreadMessages: number =
    Number((staffMsgsData as any)?.data?.counts?.unread ?? NaN) ||
    Number((unreadData as any)?.data?.unread_count ?? 0) || 0;
  // Phase 3c — parent-requests needing action; participant-scoped.
  const { data: parentReqData } = useGetMyParentRequestsQuery(
    { status: "needs_action", per_page: 1 },
    { skip: !userData, pollingInterval: 60_000 },
  );
  const parentRequestsNeedAction: number = Number((parentReqData as any)?.data?.counts?.needs_action ?? 0) || 0;
  // Phase 3b — assessment count from the self-only endpoint that
  // backs the Assessments page. RTK Query caches so navigating
  // between pages reuses this result. Assignments counts removed
  // with the sidebar entry.
  const { data: assessData } = useGetMyAssessmentsQuery({ per_page: 1 }, { skip: !userData });
  const assessmentsToReview: number = Number((assessData as any)?.data?.counts?.to_review ?? 0) || 0;

  const rawEvents: any[] = useMemo(() => {
    const raw = (data as any)?.data;
    return Array.isArray(raw) ? raw : Array.isArray(raw?.events) ? raw.events : [];
  }, [data]);

  // Sep 2026 — ABA Quick Action visibility gate. The self-only
  // /staff-portal/my-calendar response now carries a sibling
  // `has_aba_caseload` boolean (see TeamController::getMyCalendar)
  // computed from the same OR logic ProgramAssignmentController uses
  // to qualify staff as ABA-eligible: active program_student_assignments
  // row OR at least one class enrolling a Special-program student.
  // We deliberately fail CLOSED — until the payload arrives (or in the
  // degraded "old backend still deployed" case) the ABA tile stays
  // hidden. The ABA route itself keeps its own backend scope; this
  // hide is UX, not a security boundary.
  const hasAbaCaseload: boolean = Boolean((data as any)?.has_aba_caseload);

  // Sort + tag with a client-side derived `_startMs` for countdown.
  const events = useMemo(() => {
    const now = Date.now();
    return [...rawEvents]
      .map((e) => {
        const s = new Date(e.scheduled_start_at || 0).getTime();
        const en = new Date(e.scheduled_end_at || 0).getTime();
        return { ...e, _startMs: s, _endMs: en, _uiState: (e.attendance_state || "upcoming") as AttState, _msUntil: s - now };
      })
      .sort((a, b) => a._startMs - b._startMs);
  }, [rawEvents]);

  // Slice the extended window (spec §6) into today-only / tomorrow /
  // future so KPIs stay strictly today-scoped while Next Class + the
  // Coming Up card can look ahead.
  const todayEvents = useMemo(() => events.filter((e) => e.date === todayYmd), [events, todayYmd]);
  const tomorrowEvents = useMemo(() => events.filter((e) => e.date === tomorrowYmd), [events, tomorrowYmd]);
  const futureEvents = useMemo(() => events.filter((e) => e.date > todayYmd), [events, todayYmd]);

  const liveClasses = useMemo(() => todayEvents.filter((e) => e._uiState === "live"), [todayEvents]);
  const pendingAttendance = useMemo(() => todayEvents.filter((e) => e._uiState === "pending"), [todayEvents]);
  const upcomingTodayClasses = useMemo(() => todayEvents.filter((e) => e._uiState === "upcoming"), [todayEvents]);
  // Next class: live now → later today → next future day. Cancelled
  // occurrences excluded from the pick.
  const nextClass = useMemo(() => {
    if (liveClasses[0]) return liveClasses[0];
    if (upcomingTodayClasses[0]) return upcomingTodayClasses[0];
    return futureEvents.find((e) => e._uiState !== "cancelled") || null;
  }, [liveClasses, upcomingTodayClasses, futureEvents]);

  // KPIs (spec §9) — strictly today-scoped. Students Today counts
  // distinct roster members across today's non-cancelled events so a
  // Monday student never appears in Sunday's KPI even now that the
  // fetch window includes tomorrow.
  const kpis = useMemo(() => {
    const classesToday = todayEvents.filter((e) => e._uiState !== "cancelled").length;
    const studentIds = new Set<number>();
    todayEvents.forEach((e) => {
      if (e._uiState === "cancelled") return;
      (Array.isArray(e.students) ? e.students : []).forEach((s: any) => {
        if (typeof s?.id === "number") studentIds.add(s.id);
      });
    });
    return {
      classesToday,
      studentsToday: studentIds.size,
      attendanceToComplete: liveClasses.length + pendingAttendance.length,
      messages: unreadMessages,
    };
  }, [todayEvents, liveClasses.length, pendingAttendance.length, unreadMessages]);

  // Distinct students today, with a per-student class-count.
  // Uses todayEvents (not the full 7-day window) so a Monday student
  // never bleeds into Sunday's "Students Today" card.
  const studentsToday = useMemo(() => {
    const map = new Map<number, { id: number; name: string; image: string | null; count: number }>();
    todayEvents.forEach((e) => {
      if (e._uiState === "cancelled") return;
      (Array.isArray(e.students) ? e.students : []).forEach((s: any) => {
        if (typeof s?.id !== "number") return;
        const cur = map.get(s.id);
        if (cur) cur.count += 1;
        else map.set(s.id, { id: s.id, name: s.name || "Student", image: s.image ?? null, count: 1 });
      });
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [todayEvents]);

  // Priorities — ordered per spec §14. Only real data.
  const priorities = useMemo(() => {
    const items: Array<{ id: string; title: string; body: string; action?: () => void; actionLabel?: string; tone: "urgent" | "warning" | "info" }> = [];
    liveClasses.forEach((e) => items.push({
      id: `live-${e.occurrence_id}`,
      title: `Class live now: ${compactClassLabel(e)}`,
      body: `Started ${fmtTime(e.actual_start_at)} · take attendance now`,
      tone: "urgent",
      action: () => navigate(`/class-sessions/${e.occurrence_id}/attendance`),
      actionLabel: "Open Live Class",
    }));
    pendingAttendance.forEach((e) => items.push({
      id: `pend-${e.occurrence_id}`,
      title: `Attendance pending: ${compactClassLabel(e)}`,
      body: `Ended ${fmtTime(e.scheduled_end_at)} · not submitted yet`,
      tone: "warning",
      action: () => navigate(`/class-sessions/${e.occurrence_id}/attendance`),
      actionLabel: "Take Attendance",
    }));
    if (nextClass && nextClass._uiState === "upcoming" && nextClass._msUntil <= 30 * 60_000) {
      items.push({
        id: `next-${nextClass.occurrence_id}`,
        title: `Next class starts in ${fmtCountdown(nextClass._msUntil)}`,
        body: `${compactClassLabel(nextClass)} · ${fmtTime(nextClass.scheduled_start_at)}`,
        tone: "info",
        action: () => navigate(`/class-sessions/${nextClass.occurrence_id}/attendance`),
        actionLabel: "Open Class",
      });
    }
    if (unreadMessages > 0) {
      items.push({
        id: "msg",
        title: `${unreadMessages} unread message${unreadMessages === 1 ? "" : "s"}`,
        body: "Check your inbox for parent messages",
        tone: "info",
        action: () => navigate("/staff-portal/messages"),
        actionLabel: "Open Messages",
      });
    }
    if (parentRequestsNeedAction > 0) {
      items.push({
        id: "preq",
        title: `${parentRequestsNeedAction} parent request${parentRequestsNeedAction === 1 ? "" : "s"} need your review`,
        body: "Absence, makeup, or reschedule requests for your classes",
        tone: "warning",
        action: () => navigate("/staff-portal/parent-requests"),
        actionLabel: "Open Requests",
      });
    }
    if (assessmentsToReview > 0) {
      items.push({
        id: "assess",
        title: `${assessmentsToReview} assessment${assessmentsToReview === 1 ? "" : "s"} need review`,
        body: "Grade or complete pending assessments for your students",
        tone: "info",
        action: () => navigate("/staff-portal/assessments"),
        actionLabel: "Open Assessments",
      });
    }
    // "Assignments due this week" notification removed alongside the
    // sidebar entry — with the Staff Portal no longer exposing an
    // Assignments page, this alert would deep-link to a redirect and
    // land on Assessments, which lists a different data set. Better
    // to suppress than to mis-route.
    return items;
  }, [liveClasses, pendingAttendance, nextClass, unreadMessages, parentRequestsNeedAction, assessmentsToReview, navigate]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  }, []);
  const firstName = (userData?.staff || userData?.username || "").split(" ")[0] || "there";

  // Location context — if every non-cancelled event is at the same
  // location, show it; otherwise show "Multiple locations".
  const locationLabel = useMemo(() => {
    const locs = Array.from(new Set(todayEvents.filter((e) => e._uiState !== "cancelled" && e.location).map((e) => String(e.location))));
    if (locs.length === 0) return null;
    if (locs.length === 1) return locs[0];
    return `${locs.length} Locations`;
  }, [todayEvents]);

  if (userLoading) {
    return (
      <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  // ── Premium visual system (Aug 2026 dashboard redesign) ─────────
  // Every card uses this Card primitive so shadows / radii / borders
  // stay uniform. Hover lift is opt-in via `hoverable` so decorative
  // panels (hero glass card) don't animate on mouseover.
  const Card: React.FC<{ children: React.ReactNode; sx?: any; hoverable?: boolean }> = ({ children, sx, hoverable }) => (
    <Box sx={{
      bgcolor: "#fff",
      // Major dashboard cards are square/straight per spec — the
      // "bubbly" 16-18px radius is gone. Visual separation comes from
      // the border + shadow instead. Buttons / chips / icon tiles
      // keep their smaller radii inside these cards.
      borderRadius: 0,
      border: "1px solid rgba(148,163,184,0.22)",
      boxShadow: "0 6px 18px rgba(15,23,42,0.05)",
      overflow: "hidden",
      transition: "transform 180ms ease, box-shadow 180ms ease",
      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      ...(hoverable ? {
        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 14px 34px rgba(15,23,42,0.10)" },
      } : {}),
      ...sx,
    }}>{children}</Box>
  );

  // SolidTile — small rounded icon tile used in section headers and
  // KPI cards. Tone maps to a SOLID colour (was a two-stop
  // linear-gradient; the 2026-09 Full-Fill Rainbow pass forbids
  // gradients everywhere on this dashboard). Component name preserved
  // as GradientTile so downstream call sites don't need touching.
  type Tone = "blue" | "indigo" | "violet" | "cyan" | "amber" | "mint" | "rose";
  const TONE_SOLID: Record<Tone, string> = {
    blue:   "#2F6BFF",
    indigo: "#6657D9",
    violet: "#7C5CE7",
    cyan:   "#159A9C",
    amber:  "#C88A18",
    mint:   "#2F8F5B",
    rose:   "#D95C5C",
  };
  const GradientTile: React.FC<{ tone: Tone; size?: number; children: React.ReactNode }> = ({ tone, size = 34, children }) => (
    <Box sx={{
      width: size, height: size, borderRadius: "10px",
      display: "flex", alignItems: "center", justifyContent: "center",
      bgcolor: TONE_SOLID[tone], color: "#fff",
      flexShrink: 0,
      "& svg": { fontSize: size <= 30 ? 16 : 18 },
    }}>{children}</Box>
  );

  const SectionTitle: React.FC<{ icon: React.ReactNode; tone?: Tone; children: React.ReactNode; right?: React.ReactNode }> = ({ icon, tone = "blue", children, right }) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.25, pt: 2, pb: 1.25 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, color: "#17324D", fontWeight: 800, fontSize: 15 }}>
        <GradientTile tone={tone} size={30}>{icon}</GradientTile>
        {children}
      </Box>
      {right}
    </Box>
  );

  return (
    <Box sx={{
      // Removed `minHeight: 100vh` — it was painting a giant empty
      // grey strip below Coming Up whenever the content was shorter
      // than the viewport. The page now grows to fit its content.
      pb: 3,
      // Solid neutral page background (spec §"PAGE BACKGROUND"). The
      // previous radial + linear-gradient wash is banned under the
      // 2026-09 Full-Fill Rainbow pass — colour lives on the cards,
      // not the page.
      bgcolor: DASH_NEUTRAL.pageBg,
    }}>
      {/* ── HERO — light Step Up command header. Mostly white with a
             very faint blue wash, a thin blue→cyan brand accent stripe
             on top, and dark navy text. Colour lives in the KPI row
             and card accents, not the hero background.
             ── */}
      <Box sx={{
        position: "relative", overflow: "hidden",
        // Solid dark navy hero fill (spec §"TOP STAFF PORTAL HEADER"
        // — deep navy #0F2742). The 2026-09 Dark Jewel-Tone pass
        // replaces the earlier pastel-blue hero with the deepest
        // anchor colour on the dashboard so the greeting reads as
        // a strong premium banner.
        bgcolor: DASH_C.navy.bg,
        color: DASH_NEUTRAL.cardText,
        px: { xs: 2, md: 2.5 }, pt: { xs: 2.5, md: 3 }, pb: { xs: 2.5, md: 3 },
        // Outer hero remains SQUARE — internal cards stay rounded.
        borderRadius: 0,
        borderBottom: `1px solid ${DASH_C.navy.border}`,
        boxShadow: "0 8px 24px rgba(30,64,175,0.06)",
      }}>
        <Box sx={{
          position: "relative", zIndex: 1,
          display: "grid",
          // Balanced 40/60 split — no awkward centre gap.
          gridTemplateColumns: { xs: "1fr", md: "minmax(320px, 0.8fr) minmax(520px, 1.2fr)" },
          gap: { xs: 2, md: 3 }, alignItems: "center",
        }}>
        {/* LEFT — greeting */}
        <Box>
          <Typography sx={{ fontSize: 11, letterSpacing: "1.2px", textTransform: "uppercase", fontWeight: 800, color: DASH_C.blue.accent }}>
            Staff Portal
          </Typography>
          {/* Solid navy heading (was a background-clip linear-gradient
              across the glyphs — banned under the 2026-09 Full-Fill
              Rainbow pass). Solid dark navy reads cleanly on the new
              soft-blue hero fill. */}
          <Typography sx={{
            fontSize: { xs: 26, md: 30 }, fontWeight: 800,
            letterSpacing: "-0.015em", lineHeight: 1.1, mt: 0.5,
            color: DASH_NEUTRAL.cardText,
            display: "inline-block",
          }}>
            {greeting}, {firstName}
          </Typography>
          <Typography sx={{ fontSize: 13.5, mt: 0.5, color: "#64748B" }}>{fmtDate(new Date())}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: "wrap", rowGap: 1 }}>
            {locationLabel && (
              <Chip
                icon={<LocationOnOutlinedIcon sx={{ fontSize: "14px !important", color: "#FFFFFF !important" }} />}
                label={locationLabel}
                size="small"
                sx={{
                  // Dark teal pill on the navy hero (spec §"TOP STAFF
                  // PORTAL HEADER" — dark blue/teal with white text).
                  bgcolor: DASH_C.teal.bg,
                  color: "#17324D",
                  border: `1px solid ${DASH_C.teal.border}`,
                  fontWeight: 700, fontSize: 11.5,
                }}
              />
            )}
            <Chip
              label={`${kpis.classesToday} class${kpis.classesToday === 1 ? "" : "es"} today`}
              size="small"
              sx={{
                bgcolor: DASH_C.blue.bg,
                color: "#17324D",
                border: `1px solid ${DASH_C.blue.border}`,
                fontWeight: 700, fontSize: 11.5,
              }}
            />
          </Stack>
        </Box>

        {/* RIGHT — Next / Live class card. Solid indigo fill for the
            Upcoming state and solid emerald fill for Live (spec §"TOP
            STAFF WELCOME"). The previous 135° linear-gradient
            background variants are removed under the 2026-09
            Full-Fill Rainbow pass. */}
        <Box sx={{
          position: "relative",
          p: { xs: 1.75, md: 2 },
          borderRadius: 0,
          bgcolor: nextClass?._uiState === "live" ? DASH_C.emerald.bg : DASH_C.indigo.bg,
          border: `1px solid ${nextClass?._uiState === "live" ? DASH_C.emerald.border : DASH_C.indigo.border}`,
          borderLeft: `3px solid ${nextClass?._uiState === "live" ? DASH_C.emerald.accent : DASH_C.indigo.accent}`,
          boxShadow: "0 6px 16px rgba(30,64,175,0.05)",
          color: DASH_NEUTRAL.cardText,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {nextClass?._uiState === "live" && (
              <Box sx={{
                width: 10, height: 10, borderRadius: "50%", bgcolor: "#22c55e",
                boxShadow: "0 0 0 4px rgba(34,197,94,0.35)",
                animation: "pulse 1.6s ease-in-out infinite",
                "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.5 } },
              }} />
            )}
            <Typography sx={{ fontSize: 10.5, letterSpacing: "0.8px", fontWeight: 800, textTransform: "uppercase", color: nextClass?._uiState === "live" ? "#0F766E" : "#315BEA" }}>
              {nextClass?._uiState === "live" ? "Live now" : "Next class"}
            </Typography>
          </Box>
          {isLoading ? (
            <Skeleton sx={{ mt: 1 }} height={30} width="60%" />
          ) : isError ? (
            <Typography sx={{ fontSize: 13, mt: 1, color: "#B91C1C" }}>Could not load today's classes.</Typography>
          ) : !nextClass ? (
            <Typography sx={{ fontSize: 14, mt: 1, color: "#64748B" }}>
              Your teaching schedule is complete for today.
            </Typography>
          ) : (
            <>
              <Typography sx={{ fontSize: 17, fontWeight: 800, mt: 0.5, letterSpacing: "-0.01em", lineHeight: 1.15, color: "#17324D" }}>
                {compactClassLabel(nextClass)}
              </Typography>
              <Typography sx={{ fontSize: 12.5, mt: 0.35, color: "#64748B" }}>
                {fmtTime(nextClass.scheduled_start_at)} – {fmtTime(nextClass.scheduled_end_at)}
                {nextClass._uiState === "upcoming" && nextClass._msUntil > 0 && (
                  <> · Starts in <b style={{ color: "#17324D" }}>{fmtCountdown(nextClass._msUntil)}</b></>
                )}
                {nextClass._uiState === "live" && nextClass.actual_start_at && (
                  <> · Live since <b style={{ color: "#17324D" }}>{fmtTime(nextClass.actual_start_at)}</b></>
                )}
              </Typography>
              <Box sx={{ mt: 0.75, display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                {/* Semi-transparent white pills on the dark indigo
                    Next Class card so the small chips read as bright
                    tags against the jewel tone. */}
                {typeof nextClass.student_count === "number" && (
                  <Chip
                    icon={<GroupOutlinedIcon sx={{ fontSize: "12px !important", color: "#FFFFFF !important" }} />}
                    size="small"
                    label={`${nextClass.student_count} student${nextClass.student_count === 1 ? "" : "s"}`}
                    sx={{ bgcolor: "#FFFFFF", color: "#17324D", border: "1px solid rgba(23,50,77,0.14)", fontWeight: 700, fontSize: 11, height: 22 }}
                  />
                )}
                {nextClass.location && (
                  <Chip size="small" label={nextClass.location} sx={{ bgcolor: "#FFFFFF", color: "#17324D", border: "1px solid rgba(23,50,77,0.14)", fontWeight: 700, fontSize: 11, height: 22 }} />
                )}
                {nextClass.study_mode && (
                  <Chip size="small" label={nextClass.study_mode} sx={{ bgcolor: "#FFFFFF", color: "#17324D", border: "1px solid rgba(23,50,77,0.14)", fontWeight: 700, fontSize: 11, height: 22 }} />
                )}
              </Box>
              {nextClass.occurrence_id && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PlayCircleOutlineIcon sx={{ fontSize: "16px !important" }} />}
                  onClick={() => navigate(`/class-sessions/${nextClass.occurrence_id}/attendance`)}
                  sx={{
                    mt: 1.25, textTransform: "none", fontWeight: 800, borderRadius: 2,
                    // Solid blue button (was a linear-gradient
                    // background + hover-gradient combo).
                    bgcolor: DASH_C.blue.accent,
                    color: "#fff",
                    boxShadow: "0 4px 10px rgba(47,107,255,0.24)",
                    py: 0.5, px: 1.75, fontSize: 12.5,
                    "&:hover": { bgcolor: "#245BE0", boxShadow: "0 6px 14px rgba(47,107,255,0.32)" },
                  }}
                >
                  {nextClass._uiState === "live" ? "Open Live Class" : "Open Class"}
                </Button>
              )}
            </>
          )}
        </Box>
        </Box>
      </Box>

      {/* ── KPI row — premium tinted cards with gradient icon tile,
             tabular-nums metric, decorative radial glow, and hover
             lift. Tones map per spec §6: blue / cyan / amber / violet.
             ── */}
      <Box sx={{ px: { xs: 2, md: 2.5 }, mt: 2 }}>
        <Grid container spacing={2}>
          {/* KPI tone assignments per spec §"TOP METRIC CARDS":
              Classes Today → blue, Students Today → teal,
              Attendance to Complete → emerald (amber when > 0 to
              signal action needed), Unread Messages → indigo. Each
              card carries its full solid fill (was a white surface
              with a radial-gradient corner glow — glow is deleted). */}
          {([
            {
              label: "Classes Today", value: kpis.classesToday,
              tone: "blue" as Tone, palette: DASH_C.blue,
              icon: <CalendarMonthOutlinedIcon />,
              hint: `${kpis.classesToday === 1 ? "1 scheduled teaching session" : `${kpis.classesToday} scheduled sessions`}`,
            },
            {
              label: "Students Today", value: kpis.studentsToday,
              tone: "cyan" as Tone, palette: DASH_C.teal,
              icon: <GroupOutlinedIcon />,
              hint: kpis.studentsToday === 0 ? "No students scheduled" : `${kpis.studentsToday} across today's roster`,
            },
            {
              label: "Attendance To Complete", value: kpis.attendanceToComplete,
              tone: (kpis.attendanceToComplete > 0 ? "amber" : "mint") as Tone,
              palette: kpis.attendanceToComplete > 0 ? DASH_C.amber : DASH_C.emerald,
              icon:  <HowToRegOutlinedIcon />,
              hint: kpis.attendanceToComplete > 0 ? "Live or ended · not submitted" : "You're all caught up",
            },
            {
              label: "Unread Messages", value: kpis.messages,
              tone: "violet" as Tone, palette: DASH_C.indigo,
              icon: <ChatBubbleOutlineIcon />,
              hint: kpis.messages > 0 ? "Parent inbox waiting" : "Inbox is clear",
            },
          ]).map((k) => (
            <Grid item xs={6} sm={6} md={3} key={k.label}>
              <Card
                hoverable
                sx={{
                  position: "relative", overflow: "hidden",
                  bgcolor: k.palette.bg,
                  border: `1px solid ${k.palette.border}`,
                  p: 1.5,
                  cursor: "default",
                }}
              >
                <Box sx={{ position: "relative", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <GradientTile tone={k.tone} size={30}>{k.icon}</GradientTile>
                  <Typography sx={{
                    fontSize: 10, fontWeight: 800, letterSpacing: "0.5px",
                    textTransform: "uppercase", color: k.palette.accent, opacity: 0.9,
                    textAlign: "right", maxWidth: 120, lineHeight: 1.2,
                  }}>
                    {k.label}
                  </Typography>
                </Box>
                <Typography sx={{
                  position: "relative",
                  fontSize: { xs: 24, md: 28 }, fontWeight: 800, color: DASH_NEUTRAL.metric,
                  mt: 0.75, lineHeight: 1, fontVariantNumeric: "tabular-nums",
                  letterSpacing: "-0.01em",
                }}>
                  {isLoading ? "—" : k.value}
                </Typography>
                <Typography sx={{ position: "relative", fontSize: 11.5, color: k.palette.text, opacity: 0.8, mt: 0.35 }}>
                  {k.hint}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Main grid — native CSS grid so the left column can go
             wider than the MUI 12-col limit (66.6% at md=8). Target
             70/30 with a firm right-column minimum so Quick Actions
             tiles never clip. Stacks to one column under lg. ── */}
      <Box sx={{
        px: { xs: 2, md: 2.5 }, mt: 2,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2.3fr) minmax(360px, 1fr)" },
        gap: 2,
        alignItems: "start",
      }}>
        <Box sx={{ minWidth: 0 }}>
          {/* LEFT column body */}
            {/* Today's Schedule — solid blue outer fill (spec §"TODAY'S SCHEDULE"). */}
            <Card sx={{ bgcolor: DASH_C.blue.bg, border: `1px solid ${DASH_C.blue.border}` }}>
              <SectionTitle
                icon={<EventAvailableOutlinedIcon />} tone="blue"
                right={<Chip size="small" label={`${todayEvents.length} class${todayEvents.length === 1 ? "" : "es"}`} sx={{ bgcolor: "#FFFFFF", color: "#17324D", fontWeight: 800, fontSize: 10.5 }} />}
              >
                Today's Schedule
              </SectionTitle>
              {isLoading ? (
                <Box sx={{ p: 2 }}>
                  <Skeleton height={56} />
                  <Skeleton height={56} sx={{ mt: 1 }} />
                  <Skeleton height={56} sx={{ mt: 1 }} />
                </Box>
              ) : todayEvents.length === 0 ? (
                <Box sx={{ p: 3.5, textAlign: "center" }}>
                  <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 42, height: 42, borderRadius: "50%", bgcolor: DASH_C.blue.accent, color: "#FFFFFF", mb: 1 }}>
                    <EventAvailableOutlinedIcon fontSize="small" />
                  </Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#17324D" }}>Nothing scheduled today</Typography>
                  <Typography sx={{ fontSize: 12, color: "#64748B", mt: 0.25 }}>Check Coming Up for your next teaching day.</Typography>
                </Box>
              ) : (
                todayEvents.map((e, i) => {
                  const chip = stateChip(e._uiState);
                  const isReplacement = e.session_type === "makeup" || e.session_type === "extra";
                  // Left accent line colour follows the class status —
                  // spec §8: blue upcoming, mint live, amber pending,
                  // mint submitted, coral cancelled.
                  const accent =
                    e._uiState === "live" ? "#10B981"
                    : e._uiState === "pending" ? "#F59E0B"
                    : e._uiState === "submitted" ? "#10B981"
                    : e._uiState === "cancelled" ? "#EF5350"
                    : "#4F6EF7";
                  return (
                    <Box
                      key={e.occurrence_id ?? i}
                      onClick={() => e.occurrence_id && navigate(`/class-sessions/${e.occurrence_id}/attendance`)}
                      sx={{
                        position: "relative",
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "150px 1fr auto" },
                        gap: 2, alignItems: "center",
                        px: 2, py: 1.75,
                        borderTop: "1px solid rgba(23,50,77,0.08)",
                        cursor: e.occurrence_id ? "pointer" : "default",
                        opacity: e._uiState === "cancelled" ? 0.55 : 1,
                        transition: "background-color 180ms ease",
                        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                        "&::before": {
                          content: '""', position: "absolute", left: 0, top: 8, bottom: 8, width: 3,
                          borderRadius: 2, background: accent,
                        },
                        "&:hover": e.occurrence_id ? { bgcolor: alpha(accent, 0.05) } : {},
                        pl: 2.5,
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#17324D", fontVariantNumeric: "tabular-nums" }}>
                          {fmtTime(e.scheduled_start_at)}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#64748B", fontVariantNumeric: "tabular-nums" }}>
                          → {fmtTime(e.scheduled_end_at)}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: "#17324D", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {compactClassLabel(e)}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: "#64748B", mt: 0.25 }}>
                          {[e.location, e.study_mode, typeof e.student_count === "number" ? `${e.student_count} student${e.student_count === 1 ? "" : "s"}` : null]
                            .filter(Boolean).join(" · ")}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                        {isReplacement && (
                          <Chip size="small" label={e.session_type === "makeup" ? "Makeup" : "Extra"} sx={{ bgcolor: "#f5f3ff", color: "#6d28d9", fontWeight: 700, fontSize: 10.5 }} />
                        )}
                        <Chip
                          size="small"
                          label={chip.label}
                          sx={{ bgcolor: chip.bg, color: chip.fg, border: `1px solid ${chip.border}`, fontWeight: 800, fontSize: 10.5 }}
                        />
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Card>

            {/* Attendance To Complete — amber accent, only rendered
                when work is pending (spec §9). */}
            {kpis.attendanceToComplete > 0 && (
              <Card
                sx={{
                  mt: 2,
                  bgcolor: DASH_C.amber.bg,
                  border: `1px solid ${DASH_C.amber.border}`,
                  borderLeft: `4px solid ${DASH_C.amber.accent}`,
                }}
              >
                <SectionTitle
                  icon={<HowToRegOutlinedIcon />} tone="amber"
                  right={<Chip size="small" label={`${kpis.attendanceToComplete} pending`} sx={{ bgcolor: "#17324D", color: DASH_C.amber.bg, fontWeight: 800, fontSize: 10.5 }} />}
                >
                  Attendance To Complete
                </SectionTitle>
                {[...liveClasses, ...pendingAttendance].map((e) => (
                  <Box
                    key={`att-${e.occurrence_id}`}
                    sx={{
                      display: "flex", alignItems: "center", gap: 2,
                      p: 2, borderTop: "1px solid rgba(23,50,77,0.08)",
                      transition: "background-color 180ms ease",
                      "&:hover": { bgcolor: alpha("#F59E0B", 0.06) },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#17324D" }}>{compactClassLabel(e)}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: "#64748B", mt: 0.25 }}>
                        {fmtTime(e.scheduled_start_at)} – {fmtTime(e.scheduled_end_at)}
                        {typeof e.student_count === "number" && <> · {e.student_count} student{e.student_count === 1 ? "" : "s"}</>}
                        {typeof e.marked_count === "number" && typeof e.student_count === "number" && (
                          <> · {e.marked_count} / {e.student_count} marked</>
                        )}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={e._uiState === "live" ? "Live" : "Pending"}
                      sx={{
                        bgcolor: e._uiState === "live" ? "#DCFCE7" : "#FEF3C7",
                        color:   e._uiState === "live" ? "#166534" : "#92400E",
                        border:  `1px solid ${e._uiState === "live" ? "#A7F3D0" : "#FDE68A"}`,
                        fontWeight: 800, fontSize: 10.5,
                      }}
                    />
                    <Button
                      size="small" variant="contained"
                      onClick={() => navigate(`/class-sessions/${e.occurrence_id}/attendance`)}
                      sx={{
                        textTransform: "none", fontWeight: 800, borderRadius: 2,
                        // Solid amber (was linear-gradient TONE_GRADIENT.amber).
                        bgcolor: DASH_C.amber.accent,
                        color: "#fff",
                        boxShadow: "0 4px 10px rgba(200,138,24,0.28)",
                        "&:hover": { bgcolor: "#B57A15", boxShadow: "0 6px 14px rgba(200,138,24,0.36)" },
                      }}
                    >
                      Take Attendance
                    </Button>
                  </Box>
                ))}
              </Card>
            )}

            {/* Coming Up (spec §8) — surfaces tomorrow first, else the
                next non-empty teaching day in the fetch window. Hidden
                entirely when the window has no future events, so a
                fully-caught-up teacher doesn't see an empty card. */}
            {(() => {
              const upNext = tomorrowEvents.length > 0 ? tomorrowEvents : futureEvents.slice(0, 3);
              if (isLoading || upNext.length === 0) return null;
              const labelDate = new Date(upNext[0].scheduled_start_at || upNext[0].date);
              const heading = tomorrowEvents.length > 0
                ? `Tomorrow — ${fmtDate(labelDate)}`
                : `Coming Up — ${fmtDate(labelDate)}`;
              const rows = upNext.slice(0, 3);
              return (
                <Card sx={{ mt: 2, bgcolor: DASH_C.indigo.bg, border: `1px solid ${DASH_C.indigo.border}` }}>
                  <SectionTitle icon={<EventAvailableOutlinedIcon fontSize="small" />} tone="indigo">{heading}</SectionTitle>
                  {rows.map((e, i) => (
                    <Box
                      key={e.occurrence_id ?? i}
                      onClick={() => e.occurrence_id && navigate(`/class-sessions/${e.occurrence_id}/attendance`)}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "120px 1fr auto" },
                        gap: 2, alignItems: "center",
                        p: 2, borderTop: "1px solid rgba(23,50,77,0.08)",
                        cursor: e.occurrence_id ? "pointer" : "default",
                        "&:hover": e.occurrence_id ? { bgcolor: "rgba(23,50,77,0.04)" } : {},
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: theme.palette.primary.dark, fontVariantNumeric: "tabular-nums" }}>
                          {fmtTime(e.scheduled_start_at)}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#64748B" }}>→ {fmtTime(e.scheduled_end_at)}</Typography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#17324D", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {compactClassLabel(e)}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: "#64748B", mt: 0.25 }}>
                          {typeof e.student_count === "number" ? `${e.student_count} student${e.student_count === 1 ? "" : "s"}` : ""}
                          {e.location && <> · {e.location}</>}
                          {e.study_mode && <> · {e.study_mode}</>}
                        </Typography>
                      </Box>
                      <Chip size="small" label={new Date(e.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                        sx={{ bgcolor: "#eef2ff", color: "#3730a3", fontWeight: 700, fontSize: 10.5 }} />
                    </Box>
                  ))}
                  {upNext.length > 3 && (
                    <Box sx={{ p: 1.5, textAlign: "center" }}>
                      <Button size="small" onClick={() => navigate("/today")} sx={{ textTransform: "none", fontWeight: 700 }}>
                        View full schedule →
                      </Button>
                    </Box>
                  )}
                </Card>
              );
            })()}
        </Box>

        {/* RIGHT: priorities + students + quick actions */}
        <Box sx={{ minWidth: 0 }}>
            {/* Priorities — full solid coral fill (spec §"MY PRIORITIES").
                The "urgent things need your attention" cue is now carried
                by the whole card, not just the header tile. */}
            <Card sx={{ bgcolor: DASH_C.coral.bg, border: `1px solid ${DASH_C.coral.border}` }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.25, pt: 2, pb: 1.25 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, color: "#17324D", fontWeight: 800, fontSize: 15 }}>
                  <Box sx={{
                    width: 30, height: 30, borderRadius: "10px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    // Solid coral-accent tile with white icon (was a
                    // translucent rgba red on a red card, which read as
                    // a smudge under the dark jewel-tone pass).
                    bgcolor: DASH_C.coral.accent,
                    color: "#FFFFFF",
                    flexShrink: 0,
                    "& svg": { fontSize: 18 },
                  }}>
                    <PriorityHighOutlinedIcon />
                  </Box>
                  My Priorities
                </Box>
              </Box>
              {isLoading ? (
                <Box sx={{ p: 2 }}><Skeleton height={80} /></Box>
              ) : priorities.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, borderRadius: "50%", bgcolor: DASH_C.emerald.accent, color: "#FFFFFF", mb: 1 }}>
                    <CheckCircleOutlineIcon />
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#17324D" }}>All caught up</Typography>
                  <Typography sx={{ fontSize: 12, color: "#64748B", mt: 0.5 }}>
                    Nothing needs your attention right now.
                  </Typography>
                </Box>
              ) : (
                priorities.map((p) => {
                  // Both "urgent" and "warning" now render as a red alert
                  // treatment (previously "warning" was amber/yellow). The
                  // priorities feed only surfaces attention-required items,
                  // so a single red alert language makes them instantly
                  // scannable. "info" keeps its blue tone unchanged.
                  // Row treatment on the bright coral parent card:
                  // dark navy title / action text on a translucent
                  // white row overlay so the row separates cleanly
                  // from the coral card; solid strong-hue icon tile
                  // (coral accent for alerts, royal blue for info)
                  // with a white icon inside.
                  const isAlert = p.tone === "urgent" || p.tone === "warning";
                  const accent   = isAlert ? DASH_C.coral.accent : DASH_C.blue.accent;
                  const actionCol = accent;
                  const titleCol  = "#17324D";
                  const bg        = "#FFFFFF";
                  const tileBg    = accent;
                  const tileFg    = "#FFFFFF";
                  const icon      = p.tone === "urgent"
                    ? <PriorityHighOutlinedIcon />
                    : p.tone === "warning"
                    ? <HowToRegOutlinedIcon />
                    : <ScheduleOutlinedIcon />;
                  return (
                    <Box key={p.id} sx={{
                      p: 1.75, borderTop: "1px solid rgba(23,50,77,0.08)",
                      borderLeft: `3px solid ${accent}`,
                      bgcolor: bg,
                      display: "flex", gap: 1.25, alignItems: "flex-start",
                    }}>
                      <Box sx={{
                        width: 30, height: 30, borderRadius: "10px",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        bgcolor: tileBg, color: tileFg,
                        flexShrink: 0,
                        "& svg": { fontSize: 16 },
                      }}>
                        {icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: titleCol }}>{p.title}</Typography>
                        <Typography sx={{ fontSize: 11.5, color: "#64748B", mt: 0.25 }}>{p.body}</Typography>
                        {p.action && p.actionLabel && (
                          <Button
                            size="small"
                            onClick={p.action}
                            sx={{
                              textTransform: "none", mt: 0.75, fontWeight: 700,
                              px: isAlert ? 0.75 : 0, py: 0, minWidth: 0, color: actionCol,
                              "&:hover": isAlert
                                ? { bgcolor: "rgba(23,50,77,0.08)", color: "#17324D" }
                                : { bgcolor: "rgba(23,50,77,0.08)" },
                            }}
                          >
                            {p.actionLabel} →
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })
              )}
            </Card>

            {/* Students Today — solid teal fill (spec §"STUDENTS TODAY"). */}
            <Card sx={{ mt: 2, bgcolor: DASH_C.teal.bg, border: `1px solid ${DASH_C.teal.border}` }}>
              <SectionTitle
                icon={<GroupOutlinedIcon />} tone="cyan"
                right={studentsToday.length > 0
                  ? <Chip size="small" label={`${studentsToday.length} total`} sx={{ bgcolor: "#FFFFFF", color: "#17324D", fontWeight: 800, fontSize: 10.5 }} />
                  : null}
              >
                Students Today
              </SectionTitle>
              {isLoading ? (
                <Box sx={{ p: 2 }}><Skeleton height={40} /></Box>
              ) : studentsToday.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 42, height: 42, borderRadius: "50%", bgcolor: DASH_C.teal.accent, color: "#FFFFFF", mb: 1 }}>
                    <GroupOutlinedIcon fontSize="small" />
                  </Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#17324D" }}>No students scheduled today</Typography>
                  <Typography sx={{ fontSize: 11.5, color: "#64748B", mt: 0.25 }}>
                    Roster returns as soon as a class starts.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {studentsToday.slice(0, 8).map((s) => {
                    // Whole-row navigation to the Staff Portal student
                    // profile. Uses the same route as the "Recently
                    // Searched Student" shortcut so that opening a
                    // student from here also updates the staff-scoped
                    // recent-student tracking automatically.
                    const openStudent = () => navigate(`/staff-portal/students/${s.id}`);
                    return (
                      <Box
                        key={s.id}
                        role="button"
                        tabIndex={0}
                        onClick={openStudent}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openStudent();
                          }
                        }}
                        sx={{
                          display: "flex", alignItems: "center", gap: 1.25, p: 1, borderRadius: 2,
                          cursor: "pointer",
                          transition: "background-color 120ms ease, box-shadow 120ms ease",
                          "&:hover": {
                            // Semi-transparent white hover overlay on
                            // the dark teal Students Today card (was
                            // alpha primary — invisible on dark bg).
                            bgcolor: "rgba(23,50,77,0.05)",
                            "& .students-today-name": { color: "#17324D", fontWeight: 700 },
                            "& .students-today-chevron": { opacity: 1, transform: "translateX(0)" },
                          },
                          "&:focus-visible": {
                            outline: `2px solid rgba(23,50,77,0.35)`,
                            outlineOffset: 2,
                          },
                        }}
                      >
                        <Box sx={{
                          width: 30, height: 30, borderRadius: "50%",
                          // Solid teal-accent avatar circle on the dark
                          // teal card (was a low-opacity primary tint).
                          bgcolor: DASH_C.teal.accent,
                          color: "#FFFFFF",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 800, fontSize: 12,
                        }}>
                          {s.name.slice(0, 1).toUpperCase()}
                        </Box>
                        <Typography
                          className="students-today-name"
                          sx={{ fontSize: 13, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#17324D", transition: "color 120ms ease, font-weight 120ms ease" }}
                        >
                          {s.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#64748B" }}>
                          {s.count} class{s.count === 1 ? "" : "es"}
                        </Typography>
                        <ChevronRightIcon
                          className="students-today-chevron"
                          sx={{
                            fontSize: 16,
                            color: "#64748B",
                            opacity: 0,
                            transform: "translateX(-4px)",
                            transition: "opacity 120ms ease, transform 120ms ease",
                          }}
                        />
                      </Box>
                    );
                  })}
                  {studentsToday.length > 8 && (
                    <Typography sx={{ fontSize: 11, color: "#64748B", textAlign: "center", pt: 0.5 }}>
                      +{studentsToday.length - 8} more
                    </Typography>
                  )}
                </Box>
              )}
            </Card>

            {/* Quick Actions — premium tile grid. Each tile carries a
                gradient icon, a bold label, and an arrow that nudges on
                hover. Two-column on desktop, one-column on mobile so
                the tile size stays comfortable to tap. */}
            {/* Quick Actions outer container — dark slate per spec
                §"QUICK ACTIONS CONTAINER". Each tile inside then
                carries its own strong jewel-tone fill. */}
            <Card sx={{ mt: 2, bgcolor: DASH_C.slate.bg, border: `1px solid ${DASH_C.slate.border}` }}>
              <SectionTitle icon={<PlayCircleOutlineIcon />} tone="indigo">
                Quick Actions
              </SectionTitle>
              <Box sx={{ p: 1.5, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                {([
                  ...(nextClass?.occurrence_id ? [{
                    key: "att", label: "Take Attendance", tone: "amber" as Tone, palette: DASH_C.amber,
                    icon: <HowToRegOutlinedIcon />,
                    onClick: () => navigate(`/class-sessions/${nextClass.occurrence_id}/attendance`),
                    hint: nextClass?._uiState === "live" ? "Live now" : undefined,
                  }] : []),
                  { key: "sch", label: "My Schedule", tone: "blue" as Tone, palette: DASH_C.blue,
                    icon: <ScheduleOutlinedIcon />,
                    onClick: () => staffId && navigate(`/team-history/${staffId}`),
                    disabled: !staffId,
                  },
                  { key: "msg", label: "Messages", tone: "violet" as Tone, palette: DASH_C.purple,
                    icon: <ChatBubbleOutlineIcon />,
                    onClick: () => navigate("/staff-portal/messages"),
                    badge: unreadMessages > 0 ? unreadMessages : undefined,
                  },
                  { key: "req", label: "Parent Requests", tone: "rose" as Tone, palette: DASH_C.coral,
                    icon: <ChatBubbleOutlineIcon />,
                    onClick: () => navigate("/staff-portal/parent-requests"),
                    badge: parentRequestsNeedAction > 0 ? parentRequestsNeedAction : undefined,
                  },
                  { key: "ass", label: "My Assessments", tone: "cyan" as Tone, palette: DASH_C.teal,
                    icon: <AssignmentTurnedInOutlinedIcon />,
                    onClick: () => navigate("/staff-portal/assessments"),
                    badge: assessmentsToReview > 0 ? assessmentsToReview : undefined,
                  },
                  // "My Assignments" quick action removed with the
                  // sidebar entry — the route now redirects to
                  // Assessments so a lingering card here would be
                  // a mislabelled shortcut.
                  //
                  // Sep 2026 — ABA Therapy tile is CONDITIONALLY spread
                  // into the array (not rendered disabled, not hidden
                  // with CSS) so the grid reflows with no blank cell
                  // when the current staff member has no ABA caseload
                  // and no Special-student class. The `has_aba_caseload`
                  // signal is computed server-side in getMyCalendar
                  // using the same OR logic ProgramAssignmentController
                  // uses to qualify ABA staff — one source of truth.
                  ...(hasAbaCaseload ? [{ key: "aba", label: "ABA Therapy", tone: "mint" as Tone, palette: DASH_C.emerald,
                    icon: <PsychologyOutlinedIcon />,
                    onClick: () => navigate("/aba-therapy"),
                  }] : []),
                ]).map((a: any) => (
                  <Box
                    key={a.key}
                    role="button"
                    tabIndex={a.disabled ? -1 : 0}
                    onClick={a.disabled ? undefined : a.onClick}
                    onKeyDown={(e) => { if (!a.disabled && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); a.onClick?.(); } }}
                    aria-disabled={!!a.disabled}
                    sx={{
                      position: "relative",
                      display: "flex", alignItems: "center", gap: 1.25,
                      p: 1.25, borderRadius: "8px",
                      // Full solid tile fill (was a white tile + coloured
                      // border + hover-tint). Each Quick Action now
                      // carries its palette identity edge-to-edge.
                      bgcolor: a.palette.bg,
                      border: `1px solid ${a.palette.border}`,
                      cursor: a.disabled ? "not-allowed" : "pointer",
                      opacity: a.disabled ? 0.55 : 1,
                      transition: "transform 160ms ease, box-shadow 160ms ease",
                      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                      "&:hover": a.disabled ? {} : {
                        transform: "translateY(-1px)",
                        boxShadow: `0 6px 14px ${alpha(a.palette.accent, 0.20)}`,
                        borderColor: a.palette.accent,
                        "& .qa-arrow": { transform: "translateX(2px)" },
                      },
                      "&:focus-visible": { outline: `2px solid ${a.palette.accent}`, outlineOffset: 2 },
                    }}
                  >
                    <GradientTile tone={a.tone} size={34}>{a.icon}</GradientTile>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: a.palette.text }}>{a.label}</Typography>
                      {a.hint && (
                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: a.palette.accent, textTransform: "uppercase", letterSpacing: "0.5px", mt: 0.25 }}>
                          {a.hint}
                        </Typography>
                      )}
                    </Box>
                    {a.badge != null && (
                      <Chip size="small" label={a.badge} sx={{ bgcolor: "#FFFFFFCC", color: a.palette.accent, fontWeight: 800, fontSize: 10.5, height: 20, border: `1px solid ${a.palette.border}` }} />
                    )}
                    <Box className="qa-arrow" sx={{ color: a.palette.accent, fontWeight: 800, transition: "transform 160ms ease" }}>→</Box>
                  </Box>
                ))}
              </Box>
            </Card>
        </Box>
      </Box>
      <Typography sx={{ mt: 3, px: { xs: 2, md: 2.5 }, fontSize: 11, color: "#64748B", fontStyle: "italic", textAlign: "center" }}>
        Showing only classes assigned to you. Full assessments / parent-request / ABA workspace ship in follow-up phases.
      </Typography>
    </Box>
  );
};

export default StaffPortal;
